// Componente responsabile dell'overlay coverage: gestisce heatmap, contorni e tooltip.
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  Output,
  NgZone,
  OnChanges,
  OnDestroy,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import h337, { Heatmap as HeatmapInstance, HeatmapConfiguration } from 'heatmap.js';
import { contours as d3Contours } from 'd3-contour';
import { geoPath, geoTransform } from 'd3-geo';
import { isoContours as marchingIsoContours, isoBands as marchingIsoBands } from 'marchingsquares';
import { ScichartAngularComponent } from 'scichart-angular';
import { jsPDF } from 'jspdf';
import {
  HeatmapColorMap,
  NumberRange,
  NumericAxis,
  SciChartSurface,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries
} from 'scichart';
import { libraryVersion } from 'scichart/Core/BuildStamp';
import { TGradientStop } from 'scichart/types/TGradientStop';

import { AccessPoint, Obstacle } from '../../services/commons/synthetic-grid.service';
import { HeatPoint } from '../../model/heat-point/heat-point';
import { TranslationService } from '../../services/translation.service';
import { CoverageCanvasAction, CoverageInteractionTool, CoverageSelection } from './coverage-interaction.models';

type CoverageMode =
  | 'heatmap'
  | 'heatmap-soft'
  | 'scichart'
  | 'contours'
  | 'contours-lines'
  | 'contours-marching'
  | 'contours-heatmapjs';

interface ContourPath {
  d: string;
  color: string;
  value: number;
}

interface TooltipState {
  x: number;
  y: number;
  value: number;
}

@Component({
  selector: 'app-floorplan-coverage',
  standalone: true,
  imports: [CommonModule, ScichartAngularComponent],
  templateUrl: './floorplan-coverage.component.html',
  styleUrls: ['./floorplan-coverage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FloorplanCoverageComponent implements AfterViewInit, OnChanges, OnDestroy {
  protected readonly i18n = inject(TranslationService);
  @Input() floorplanUrl = '';
  @Input() imageWidth = 1;
  @Input() imageHeight = 1;
  @Input() points: HeatPoint[] = [];
  @Input() mode: CoverageMode = 'heatmap';
  @Input() accessPoints: AccessPoint[] = [];
  @Input() obstacles: Obstacle[] = [];
  @Input() heatmapOpacity = 0.9;
  @Input() interactionTool: CoverageInteractionTool = 'select';

  @Output() accessPointDropped = new EventEmitter<{ index: number; x: number; y: number; x2: number; y2: number; height?: number }>();
  @Output() canvasAction = new EventEmitter<CoverageCanvasAction>();
  @Output() measurementChange = new EventEmitter<number | undefined>();
  @Output() obstacleChanged = new EventEmitter<{
    index: number;
    x: number;
    y: number;
    x2: number;
    y2: number;
    material: string;
    loss: number;
    thickness?: number;
    height?: number;
  }>();
  @Output() signalHover = new EventEmitter<number | undefined>();
  @Output() contextMenuChange = new EventEmitter<CoverageSelection | undefined>();

  @ViewChild('container', { static: true }) private containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('heatmapLayer', { static: true }) private heatmapLayerRef!: ElementRef<HTMLDivElement>;

  // Soglie in dBm usate da entrambe le visualizzazioni.
  protected readonly thresholds = [20, 30, 40, 50, 55, 60, 65, 70, 75, 80, 85, 90];
  // Palette a 12 colori allineata alle soglie.
  protected readonly palette = [
    '#d50000',
    '#ff8f00',
    '#ffeb3b',
    '#9ccc65',
    '#4caf50',
    '#00bfa5',
    '#009cdc',
    '#0074d9',
    '#6f42c1',
    '#5e2ca5',
    '#4c1c82',
    '#3b0d52'
  ];
  protected contourPaths: ContourPath[] = [];
  protected viewBox = '0 0 0 0';
  // Tooltip mostrato al passaggio del mouse.
  protected tooltip?: TooltipState;
  protected draftObstacle?: { start: { x: number; y: number }; end: { x: number; y: number } };
  protected measurementDraft?: { start: { x: number; y: number }; end: { x: number; y: number }; locked: boolean };

  private resizeObserver?: ResizeObserver;
  private heatmap?: HeatmapInstance;
  private containerWidth = 0;
  private containerHeight = 0;
  private renderQueued = false;
  protected readonly initSciChart = async (rootElement: string | HTMLDivElement) => {
    await this.createSciChartSurface(rootElement);
    this.updateSciChartHeatmap();
    return { sciChartSurface: this.sciChartSurface! };
  };
  private sciChartConfigured = false;
  private sciChartSurface?: SciChartSurface;
  private sciChartSeries?: UniformHeatmapRenderableSeries;
  private sciChartDataSeries?: UniformHeatmapDataSeries;
  protected draggingAp?: {
    index: number;
    handle: 'start' | 'end' | 'center';
    start: { x: number; y: number };
    end: { x: number; y: number };
    pointerStart: { x: number; y: number };
    delta: { dx: number; dy: number };
  };
  private draggingPointerId?: number;
  protected draggingObstacle?: {
    index: number;
    handle: 'start' | 'end' | 'center';
    start: { x: number; y: number };
    end: { x: number; y: number };
    pointerStart: { x: number; y: number };
    delta: { dx: number; dy: number };
    connected?: number[];
  };

  constructor(private cdr: ChangeDetectorRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.initializeHeatmap();
    this.updateContainerSize(true);
    this.setupResizeObserver();
    this.queueRender();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageWidth'] || changes['imageHeight']) {
      this.updateContainerSize();
    }
    if (changes['mode'] || changes['points'] || changes['imageWidth'] || changes['imageHeight']) {
      this.queueRender();
    }
    if (changes['mode']) {
      this.updateHeatmapAppearance();
    }
    if (changes['heatmapOpacity']) {
      this.updateSciChartOpacity();
    }
    if (changes['interactionTool']) {
      this.draftObstacle = undefined;
      if (this.interactionTool !== 'measure') {
        this.measurementDraft = undefined;
        this.measurementChange.emit(undefined);
      }
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.renderQueued = false;
    if (this.heatmap) {
      this.heatmapLayerRef.nativeElement.innerHTML = '';
      // Heatmap.js does its own cleanup when container is disposed.
      this.heatmap = undefined;
    }
  }

  // Esporta il contenuto visibile in PDF costruendo un canvas composito con i layer esistenti.
  public async exportAsPdf(meta?: { title?: string; subtitle?: string; timestamp?: string; footer?: string }): Promise<void> {
    const container = this.containerRef?.nativeElement;
    if (!container) {
      return;
    }

    const width = container.clientWidth;
    const height = container.clientHeight;
    if (!width || !height) {
      return;
    }

    const composite = document.createElement('canvas');
    composite.width = width;
    composite.height = height;
    const ctx = composite.getContext('2d');
    if (!ctx) {
      return;
    }

    await this.drawBackground(ctx, width, height);
    // Disegna overlay in ordine corretto rispettando le trasparenze.
    this.drawActiveCanvases(ctx, width, height); // heatmap/scichart (già raster)
    if (this.mode === 'contours-marching') {
      this.drawContourPaths(ctx);
    } else {
      await this.drawSvg(ctx, container, width, height); // contours SVG con alpha
    }
    ctx.globalCompositeOperation = 'source-over';
    this.drawObstacles(ctx, width, height); // ostacoli vettoriali
    this.drawAntennas(ctx, width, height); // antenne vettoriali

    const imgData = composite.toDataURL('image/png');
    const title = meta?.title ?? 'Coverage Export';
    const subtitle = meta?.subtitle ?? '';
    const timestamp = meta?.timestamp ?? new Date().toLocaleString();
    const footer = meta?.footer ?? '';

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const headerHeight = 20;
    const footerHeight = footer ? 10 : 0;
    const availableHeight = pageHeight - headerHeight - footerHeight;

    const ratio = Math.min(pageWidth / width, availableHeight / height);
    const renderWidth = width * ratio;
    const renderHeight = height * ratio;
    const x = (pageWidth - renderWidth) / 2;
    const y = headerHeight + (availableHeight - renderHeight) / 2;

    pdf.setFillColor(15, 17, 26);
    pdf.rect(0, 0, pageWidth, headerHeight, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.text(title, 8, 12);
    pdf.setFontSize(10);
    pdf.text(subtitle, 8, 18);
    pdf.text(`Timestamp: ${timestamp}`, pageWidth - 8, 12, { align: 'right' });
    pdf.addImage(imgData, 'PNG', x, y, renderWidth, renderHeight);
    if (footer) {
      pdf.setTextColor(200, 200, 200);
      pdf.setFontSize(9);
      pdf.text(footer, pageWidth / 2, pageHeight - 4, { align: 'center' });
    }

    pdf.save(`coverage-${Date.now()}.pdf`);
  }

  private async drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number): Promise<void> {
    if (!this.floorplanUrl) {
      return;
    }
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        resolve();
      };
      img.onerror = () => resolve();
      img.src = this.floorplanUrl;
    });
  }

  private drawActiveCanvases(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const drawCanvas = (canvas?: HTMLCanvasElement | null, layerOpacity = 1) => {
      if (!canvas) {
        return;
      }
      // Disegna rispettando alpha/mix-blend-mode del canvas sorgente e l'opacità del layer wrapper.
      ctx.save();
      const style = getComputedStyle(canvas);
      const opacity = parseFloat(style.opacity || '1');
      const effectiveOpacity = (Number.isFinite(opacity) ? opacity : 1) * layerOpacity;
      ctx.globalAlpha = Math.max(0, Math.min(1, effectiveOpacity));
      const blend = (style as any)['mix-blend-mode'] ?? style.getPropertyValue('mix-blend-mode');
      if (blend && blend !== 'normal') {
        ctx.globalCompositeOperation = blend as GlobalCompositeOperation;
      } else {
        ctx.globalCompositeOperation = 'source-over';
      }
      try {
        ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, width, height);
      } catch {
        // ignore
      }
      ctx.restore();
    };

    if (this.mode === 'heatmap' || this.mode === 'heatmap-soft') {
      const layerOpacity = parseFloat(getComputedStyle(this.heatmapLayerRef.nativeElement).opacity || '1');
      const canvases = Array.from(this.heatmapLayerRef?.nativeElement.querySelectorAll('canvas') ?? []);
      const mainCanvas = (canvases[canvases.length - 1] as HTMLCanvasElement | undefined) ?? (canvases[0] as HTMLCanvasElement | undefined);
      if (mainCanvas) {
        drawCanvas(mainCanvas, layerOpacity);
      }
    } else if (this.mode === 'scichart') {
      const sciCharts = Array.from(
        this.containerRef?.nativeElement.querySelectorAll('.overlay.scichart canvas') ?? []
      ) as HTMLCanvasElement[];
      const scichartLayer = this.containerRef?.nativeElement.querySelector('.overlay.scichart') as HTMLElement | null;
      const layerOpacity = scichartLayer ? parseFloat(getComputedStyle(scichartLayer).opacity || '1') : 1;
      sciCharts.forEach((c) => drawCanvas(c, layerOpacity));
    }
  }

  private async drawSvg(ctx: CanvasRenderingContext2D, container: HTMLElement, width: number, height: number): Promise<void> {
    const svg = container.querySelector('svg.contours');
    if (!svg) {
      return;
    }
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        const style = getComputedStyle(svg);
        const opacity = parseFloat(style.opacity || '1');
        const firstPath = svg.querySelector('path');
        const pathStyle = firstPath ? getComputedStyle(firstPath) : null;
        const blend =
          (style as any)['mix-blend-mode'] ||
          style.getPropertyValue('mix-blend-mode') ||
          pathStyle?.getPropertyValue('mix-blend-mode') ||
          (pathStyle as any)?.['mix-blend-mode'];
        ctx.save();
        ctx.globalAlpha = Number.isFinite(opacity) ? opacity : 1;
        ctx.globalCompositeOperation = blend && blend !== 'normal' ? (blend as GlobalCompositeOperation) : 'source-over';
        ctx.drawImage(img, 0, 0, width, height);
        ctx.restore();
        URL.revokeObjectURL(url);
        resolve();
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
      img.src = url;
    });
  }

  private drawObstacles(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const toPxX = (x: number) => (x / Math.max(this.imageWidth, 1)) * width;
    const toPxY = (y: number) => (y / Math.max(this.imageHeight, 1)) * height;
    for (const ob of this.obstacles) {
      ctx.save();
      ctx.strokeStyle = this.obstacleStrokeColor(ob);
      ctx.lineWidth = this.obstacleStrokeWidth(ob);
      ctx.beginPath();
      ctx.moveTo(toPxX(ob.x), toPxY(ob.y));
      ctx.lineTo(toPxX(ob.x2), toPxY(ob.y2));
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawAntennas(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    const toPxX = (x: number) => (x / Math.max(this.imageWidth, 1)) * width;
    const toPxY = (y: number) => (y / Math.max(this.imageHeight, 1)) * height;
    this.accessPoints.forEach((ap, idx) => {
      const startX = toPxX(ap.x);
      const startY = toPxY(ap.y);
      const endX = toPxX(ap.x2 ?? ap.x);
      const endY = toPxY(ap.y2 ?? ap.y);
      ctx.save();
      ctx.strokeStyle = '#6be0ff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      ctx.fillStyle = '#0f172a';
      ctx.strokeStyle = '#b3e5fc';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(startX, startY, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#b3e5fc';
      ctx.font = '12px Arial';
      ctx.fillText(`ANT ${idx + 1}`, startX + 12, startY - 8);
      ctx.restore();
    });
  }

  private drawContourPaths(ctx: CanvasRenderingContext2D): void {
    const strokeOnly = this.mode === 'contours-lines';
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = this.clampedHeatmapOpacity();
    for (const path of this.contourPaths) {
      const p = new Path2D(path.d);
      if (!strokeOnly) {
        const color = this.hexToRgba(path.color, 0.7);
        ctx.fillStyle = color;
        ctx.fill(p);
      }
      ctx.strokeStyle = path.color;
      ctx.lineWidth = strokeOnly ? 2 : 1.25;
      if (strokeOnly) {
        ctx.setLineDash([6, 6]);
      } else {
        ctx.setLineDash([]);
      }
      ctx.stroke(p);
    }
    ctx.restore();
  }

  private hexToRgba(hex: string, alpha: number): string {
    const normalized = hex.replace('#', '');
    const bigint = parseInt(normalized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateContainerSize();
    this.queueRender();
  }

  // Aggiorna il tooltip calcolando il valore interpolato nel punto.
  protected onPointerMove(event: PointerEvent): void {
    if (!this.containerWidth || !this.containerHeight) {
      return;
    }
    const coords = this.pointerToImageCoords(event);
    const relativeX = coords.relativeX;
    const relativeY = coords.relativeY;
    const imageX = coords.imageX;
    const imageY = coords.imageY;
    const value = this.interpolateValue(imageX, imageY);
    this.tooltip = { x: relativeX, y: relativeY, value };
    this.signalHover.emit(value);
    if (this.interactionTool === 'draw-obstacle' && this.draftObstacle) {
      this.draftObstacle = {
        ...this.draftObstacle,
        end: { x: imageX, y: imageY }
      };
      this.cdr.markForCheck();
    }
    if (this.interactionTool === 'measure' && this.measurementDraft && !this.measurementDraft.locked) {
      this.measurementDraft = {
        ...this.measurementDraft,
        end: { x: imageX, y: imageY }
      };
      this.measurementChange.emit(this.measurementDistance());
      this.cdr.markForCheck();
    }
    if (this.draggingAp) {
      const dx = imageX - this.draggingAp.pointerStart.x;
      const dy = imageY - this.draggingAp.pointerStart.y;
      if (this.draggingAp.handle === 'start') {
        this.draggingAp = {
          ...this.draggingAp,
          start: { x: imageX, y: imageY },
          delta: { dx, dy }
        };
      } else if (this.draggingAp.handle === 'end') {
        this.draggingAp = {
          ...this.draggingAp,
          end: { x: imageX, y: imageY },
          delta: { dx, dy }
        };
      } else {
        this.draggingAp = {
          ...this.draggingAp,
          delta: { dx, dy }
        };
      }
    }
    if (this.draggingObstacle) {
      const dx = imageX - this.draggingObstacle.pointerStart.x;
      const dy = imageY - this.draggingObstacle.pointerStart.y;
      if (this.draggingObstacle.handle === 'start') {
        this.draggingObstacle = {
          ...this.draggingObstacle,
          start: { x: imageX, y: imageY },
          delta: { dx, dy }
        };
      } else if (this.draggingObstacle.handle === 'end') {
        this.draggingObstacle = {
          ...this.draggingObstacle,
          end: { x: imageX, y: imageY },
          delta: { dx, dy }
        };
      } else {
        this.draggingObstacle = {
          ...this.draggingObstacle,
          delta: { dx, dy }
        };
      }
    }
  }

  protected onPointerLeave(): void {
    this.tooltip = undefined;
    this.signalHover.emit(undefined);
  }

  protected onPointerCancel(): void {
    this.endApDrag();
  }

  protected onContainerPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const coords = this.pointerToImageCoords(event);
    if (this.interactionTool === 'place-ap') {
      event.preventDefault();
      this.canvasAction.emit({ kind: 'place-ap', x: coords.imageX, y: coords.imageY });
      return;
    }
    if (this.interactionTool === 'draw-obstacle') {
      event.preventDefault();
      this.closeContextMenu();
      if (!this.draftObstacle) {
        this.draftObstacle = {
          start: { x: coords.imageX, y: coords.imageY },
          end: { x: coords.imageX, y: coords.imageY }
        };
      } else {
        this.canvasAction.emit({
          kind: 'draw-obstacle',
          startX: this.draftObstacle.start.x,
          startY: this.draftObstacle.start.y,
          endX: coords.imageX,
          endY: coords.imageY
        });
        this.draftObstacle = undefined;
      }
      this.cdr.markForCheck();
      return;
    }
    if (this.interactionTool === 'measure') {
      event.preventDefault();
      this.closeContextMenu();
      if (!this.measurementDraft || this.measurementDraft.locked) {
        this.measurementDraft = {
          start: { x: coords.imageX, y: coords.imageY },
          end: { x: coords.imageX, y: coords.imageY },
          locked: false
        };
        this.measurementChange.emit(undefined);
      } else {
        this.measurementDraft = {
          ...this.measurementDraft,
          end: { x: coords.imageX, y: coords.imageY },
          locked: true
        };
        this.measurementChange.emit(this.measurementDistance());
      }
      this.cdr.markForCheck();
      return;
    }
    if (this.interactionTool === 'select') {
      this.closeContextMenu();
    }
  }

  protected onPointerUp(event: PointerEvent): void {
    if (this.draggingAp) {
      const positions = this.antennaCoords(this.accessPoints[this.draggingAp.index], this.draggingAp.index);
      const drop = {
        index: this.draggingAp.index,
        x: positions.start.x,
        y: positions.start.y,
        x2: positions.end.x,
        y2: positions.end.y,
        height: this.accessPoints[this.draggingAp.index]?.height ?? 7
      };
      this.draggingAp = undefined;
      this.accessPointDropped.emit(drop);
    }
    if (this.draggingObstacle) {
      const positions = this.obstacleCoords(this.obstacles[this.draggingObstacle.index], this.draggingObstacle.index);
      const baseObstacle = this.obstacles[this.draggingObstacle.index];
      const drop = {
        index: this.draggingObstacle.index,
        x: positions.start.x,
        y: positions.start.y,
        x2: positions.end.x,
        y2: positions.end.y,
        material: baseObstacle?.material ?? 'custom',
        loss: baseObstacle?.loss ?? 0,
        thickness: baseObstacle?.thickness,
        height: baseObstacle?.height
      };
      const group = this.draggingObstacle.connected ?? [this.draggingObstacle.index];
      this.draggingObstacle = undefined;
      // Emit updates for all connected obstacles using the same delta transform.
      this.obstacleChanged.emit(drop);
      if (group.length > 1) {
        const dx = drop.x - (baseObstacle?.x ?? drop.x);
        const dy = drop.y - (baseObstacle?.y ?? drop.y);
        for (const idx of group) {
          if (idx === drop.index) {
            continue;
          }
          const obs = this.obstacles[idx];
          if (!obs) {
            continue;
          }
          this.obstacleChanged.emit({
            index: idx,
            x: obs.x + dx,
            y: obs.y + dy,
            x2: obs.x2 + dx,
            y2: obs.y2 + dy,
            material: obs.material,
            loss: obs.loss,
            thickness: obs.thickness,
            height: obs.height
          });
        }
      }
    }
    if (typeof this.draggingPointerId === 'number') {
      this.containerRef.nativeElement.releasePointerCapture?.(this.draggingPointerId);
    }
    this.draggingPointerId = undefined;
  }

  protected onApPointerDown(event: PointerEvent, index: number, handle: 'start' | 'end' | 'center'): void {
    if (this.interactionTool !== 'select') {
      return;
    }
    if (event.button === 2) {
      // Right click: let the context menu handler show details without starting a drag.
      event.stopPropagation();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const coords = this.pointerToImageCoords(event);
    const currentCoords = this.antennaCoords(this.accessPoints[index], index);
    this.draggingAp = {
      index,
      handle,
      start: currentCoords.start,
      end: currentCoords.end,
      pointerStart: { x: coords.imageX, y: coords.imageY },
      delta: { dx: 0, dy: 0 }
    };
    this.tooltip = undefined;
    this.signalHover.emit(undefined);
    this.draggingPointerId = event.pointerId;
    this.containerRef.nativeElement.setPointerCapture?.(event.pointerId);
  }

  protected onObstaclePointerDown(event: PointerEvent, index: number, handle: 'start' | 'end' | 'center'): void {
    if (this.interactionTool !== 'select') {
      return;
    }
    if (event.button === 2) {
      event.stopPropagation();
      return;
    }
    if (!this.obstacles[index]) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const coords = this.pointerToImageCoords(event);
    const currentCoords = this.obstacleCoords(this.obstacles[index], index);
    const connected = handle === 'center' ? this.findConnectedObstacles(index) : undefined;
    this.draggingObstacle = {
      index,
      handle,
      start: currentCoords.start,
      end: currentCoords.end,
      pointerStart: { x: coords.imageX, y: coords.imageY },
      delta: { dx: 0, dy: 0 },
      connected
    };
    this.tooltip = undefined;
    this.signalHover.emit(undefined);
    this.draggingPointerId = event.pointerId;
    this.containerRef.nativeElement.setPointerCapture?.(event.pointerId);
  }

  protected onAntennaContextMenu(event: MouseEvent, index: number): void {
    if (this.interactionTool !== 'select') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuChange.emit({ kind: 'antenna', index });
  }

  protected onObstacleContextMenu(event: MouseEvent, index: number): void {
    if (this.interactionTool !== 'select') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (!this.obstacles[index]) {
      return;
    }
    this.contextMenuChange.emit({ kind: 'obstacle', index });
  }

  protected closeContextMenu(): void {
    this.contextMenuChange.emit(undefined);
  }

  protected onAntennaSelect(event: MouseEvent, index: number): void {
    if (this.interactionTool !== 'select') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuChange.emit({ kind: 'antenna', index });
  }

  protected onObstacleSelect(event: MouseEvent, index: number): void {
    if (this.interactionTool !== 'select') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.contextMenuChange.emit({ kind: 'obstacle', index });
  }

  protected apPositionX(ap: AccessPoint, index: number, handle: 'start' | 'end'): number {
    if (!this.containerWidth) {
      return 0;
    }
    const coords = this.antennaCoords(ap, index);
    const x = handle === 'start' ? coords.start.x : coords.end.x;
    return (x / Math.max(this.imageWidth, 1)) * this.containerWidth;
  }

  protected apPositionY(ap: AccessPoint, index: number, handle: 'start' | 'end'): number {
    if (!this.containerHeight) {
      return 0;
    }
    const coords = this.antennaCoords(ap, index);
    const y = handle === 'start' ? coords.start.y : coords.end.y;
    return (y / Math.max(this.imageHeight, 1)) * this.containerHeight;
  }

  protected obstaclePositionX(obstacle: Obstacle, index: number, handle: 'start' | 'end'): number {
    if (!this.containerWidth) {
      return 0;
    }
    const coords = this.obstacleCoords(obstacle, index);
    const x = handle === 'start' ? coords.start.x : coords.end.x;
    return (x / Math.max(this.imageWidth, 1)) * this.containerWidth;
  }

  protected obstaclePositionY(obstacle: Obstacle, index: number, handle: 'start' | 'end'): number {
    if (!this.containerHeight) {
      return 0;
    }
    const coords = this.obstacleCoords(obstacle, index);
    const y = handle === 'start' ? coords.start.y : coords.end.y;
    return (y / Math.max(this.imageHeight, 1)) * this.containerHeight;
  }

  protected obstacleStrokeColor(obstacle: Obstacle): string {
    switch (obstacle.material) {
      case 'concrete':
        return '#b0bec5';
      case 'glass':
        return '#7cd1f9';
      case 'wood':
        return '#d0a56f';
      case 'metal':
        return '#9ea7ad';
      case 'drywall':
      default:
        return '#f2c98d';
    }
  }

  protected obstacleStrokeWidth(obstacle: Obstacle): number {
    const thickness = obstacle.thickness ?? 30;
    const scale = this.imageWidth && this.containerWidth ? this.containerWidth / this.imageWidth : 1;
    return Math.max(2, thickness * scale);
  }

  protected draftPositionX(point?: { x: number; y: number }): number {
    if (!this.containerWidth || !point) {
      return 0;
    }
    return (point.x / Math.max(this.imageWidth, 1)) * this.containerWidth;
  }

  protected draftPositionY(point?: { x: number; y: number }): number {
    if (!this.containerHeight || !point) {
      return 0;
    }
    return (point.y / Math.max(this.imageHeight, 1)) * this.containerHeight;
  }

  protected measurementLabelX(): number {
    if (!this.measurementDraft) {
      return 0;
    }
    return (this.draftPositionX(this.measurementDraft.start) + this.draftPositionX(this.measurementDraft.end)) / 2;
  }

  protected measurementLabelY(): number {
    if (!this.measurementDraft) {
      return 0;
    }
    return (this.draftPositionY(this.measurementDraft.start) + this.draftPositionY(this.measurementDraft.end)) / 2;
  }

  protected measurementDistance(): number | undefined {
    if (!this.measurementDraft) {
      return undefined;
    }
    return Math.round(Math.hypot(
      this.measurementDraft.end.x - this.measurementDraft.start.x,
      this.measurementDraft.end.y - this.measurementDraft.start.y
    ) * 10) / 10;
  }

  private antennaCoords(
    ap: AccessPoint,
    index: number
  ): { start: { x: number; y: number }; end: { x: number; y: number } } {
    const start = { x: ap.x, y: ap.y };
    const end = { x: ap.x2 ?? ap.x, y: ap.y2 ?? ap.y };
    if (this.draggingAp?.index === index) {
      if (this.draggingAp.handle === 'start') {
        start.x = this.draggingAp.start.x;
        start.y = this.draggingAp.start.y;
      } else if (this.draggingAp.handle === 'end') {
        end.x = this.draggingAp.end.x;
        end.y = this.draggingAp.end.y;
      } else {
        const dx = this.draggingAp.delta.dx;
        const dy = this.draggingAp.delta.dy;
        start.x += dx;
        start.y += dy;
        end.x += dx;
        end.y += dy;
      }
    }
    return { start, end };
  }

  private obstacleCoords(
    obstacle: Obstacle,
    index: number
  ): { start: { x: number; y: number }; end: { x: number; y: number } } {
    const start = { x: obstacle.x, y: obstacle.y };
    const end = { x: obstacle.x2, y: obstacle.y2 };
    if (this.draggingObstacle) {
      const applies =
        this.draggingObstacle.index === index ||
        (this.draggingObstacle.handle === 'center' && this.draggingObstacle.connected?.includes(index));
      if (applies) {
        if (this.draggingObstacle.handle === 'start' && this.draggingObstacle.index === index) {
          start.x = this.draggingObstacle.start.x;
          start.y = this.draggingObstacle.start.y;
        } else if (this.draggingObstacle.handle === 'end' && this.draggingObstacle.index === index) {
          end.x = this.draggingObstacle.end.x;
          end.y = this.draggingObstacle.end.y;
        } else {
          const dx = this.draggingObstacle.delta.dx;
          const dy = this.draggingObstacle.delta.dy;
          start.x += dx;
          start.y += dy;
          end.x += dx;
          end.y += dy;
        }
      }
    }
    return { start, end };
  }

  private findConnectedObstacles(index: number): number[] {
    const tolerance = 6;
    const eq = (a: number, b: number) => Math.abs(a - b) <= tolerance;
    const sharesVertex = (a: Obstacle, b: Obstacle) =>
      (eq(a.x, b.x) && eq(a.y, b.y)) ||
      (eq(a.x, b.x2) && eq(a.y, b.y2)) ||
      (eq(a.x2, b.x) && eq(a.y2, b.y)) ||
      (eq(a.x2, b.x2) && eq(a.y2, b.y2));

    const visited = new Set<number>();
    const stack: number[] = [index];
    while (stack.length) {
      const current = stack.pop()!;
      if (visited.has(current)) {
        continue;
      }
      visited.add(current);
      for (let i = 0; i < this.obstacles.length; i++) {
        if (visited.has(i)) {
          continue;
        }
        if (sharesVertex(this.obstacles[current], this.obstacles[i])) {
          stack.push(i);
        }
      }
    }
    return Array.from(visited);
  }

  protected trackByPath = (_: number, item: ContourPath) => item.d;

  // Configura heatmap.js con gradient e dimensioni correnti.
  private initializeHeatmap(): void {
    const appearance = this.computeHeatmapAppearance();
    const config: HeatmapConfiguration = {
      container: this.heatmapLayerRef.nativeElement,
      radius: appearance.radius,
      maxOpacity: appearance.maxOpacity,
      blur: appearance.blur,
      gradient: this.buildGradient()
    };

    this.heatmap = h337.create(config);
    this.enableHeatmapWillReadFrequently();
    this.patchHeatmapRenderer();
  }

  /**
   * heatmap.js assigns to ImageData#data which is read-only in strict mode.
   * This replaces the renderer colorize logic with a copy that only mutates
   * the buffer and avoids reassigning the property, preventing runtime errors.
   */
  private patchHeatmapRenderer(): void {
    const renderer = (this.heatmap as unknown as { _renderer?: any })?._renderer;
    if (!renderer || renderer.__patchedColorize || typeof renderer._colorize !== 'function') {
      return;
    }

    renderer._colorize = function thisPatchedColorize(this: any) {
      let x = this._renderBoundaries[0];
      let y = this._renderBoundaries[1];
      let width = this._renderBoundaries[2] - x;
      let height = this._renderBoundaries[3] - y;

      const maxWidth = this._width;
      const maxHeight = this._height;
      const opacity = this._opacity;
      const maxOpacity = this._maxOpacity;
      const minOpacity = this._minOpacity;
      const useGradientOpacity = this._useGradientOpacity;

      if (x < 0) {
        x = 0;
      }
      if (y < 0) {
        y = 0;
      }
      if (x + width > maxWidth) {
        width = maxWidth - x;
      }
      if (y + height > maxHeight) {
        height = maxHeight - y;
      }

      const img = this.shadowCtx.getImageData(x, y, width, height);
      const imgData: Uint8ClampedArray = img.data;
      const len = imgData.length;
      const palette = this._palette;

      for (let i = 3; i < len; i += 4) {
        const alpha = imgData[i];
        const offset = alpha * 4;

        if (!offset) {
          continue;
        }

        let finalAlpha: number;
        if (opacity > 0) {
          finalAlpha = opacity;
        } else if (alpha < maxOpacity) {
          finalAlpha = alpha < minOpacity ? minOpacity : alpha;
        } else {
          finalAlpha = maxOpacity;
        }

        imgData[i - 3] = palette[offset];
        imgData[i - 2] = palette[offset + 1];
        imgData[i - 1] = palette[offset + 2];
        imgData[i] = useGradientOpacity ? palette[offset + 3] : finalAlpha;
      }

      this.ctx.putImageData(img, x, y);
      this._renderBoundaries = [1000, 1000, 0, 0];
    };

    renderer.__patchedColorize = true;
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateContainerSize();
        this.queueRender();
      });
      this.resizeObserver.observe(this.containerRef.nativeElement);
    });
  }

  private updateContainerSize(force = false): void {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    if (!force && width === this.containerWidth && height === this.containerHeight) {
      return;
    }

    this.containerWidth = width;
    this.containerHeight = height;
    this.viewBox = `0 0 ${width} ${height}`;
    this.resizeOverlays();
    this.updateHeatmapAppearance();
  }

  private queueRender(): void {
    if (this.renderQueued) {
      return;
    }

    this.renderQueued = true;
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.renderQueued = false;
        this.render();
      });
    });
  }

  // Decide quale modalità aggiornare in base allo stato corrente.
  private render(): void {
    if (!this.containerWidth || !this.containerHeight) {
      this.updateContainerSize();
    }

    if (!this.containerWidth || !this.containerHeight) {
      return;
    }

    if (this.mode === 'scichart') {
      this.renderSciChart();
    } else if (this.isHeatmapJsMode()) {
      this.renderHeatmap();
    } else if (this.mode === 'contours-heatmapjs') {
      this.renderContoursFromHeatmap();
    } else if (this.isD3ContourMode()) {
      this.renderContoursD3();
    } else {
      this.renderContoursMarchingSquares();
    }

    this.cdr.markForCheck();
  }

  // Aggiorna il canvas heatmap con il dataset interpolato.
  private renderHeatmap(): void {
    if (!this.heatmap) {
      return;
    }

    this.updateHeatmapAppearance();

    if (!this.points?.length) {
      this.heatmap.setData({ data: [], min: 0, max: 0 });
      return;
    }

    const { dataPoints } = this.generateHeatmapField();
    const minValue = this.thresholds[0];
    const maxValue = this.thresholds[this.thresholds.length - 1];
    this.heatmap.setData({ min: minValue, max: maxValue, data: dataPoints });
  }

  // Genera contorni leggendo direttamente il buffer di heatmap.js per massimizzare le prestazioni.
  private renderContoursFromHeatmap(): void {
    if (!this.heatmap) {
      this.initializeHeatmap();
    }
    if (!this.heatmap) {
      this.contourPaths = [];
      return;
    }

    this.renderHeatmap(); // assicura che il buffer heatmap sia aggiornato ai punti correnti.
    if (!this.points?.length) {
      this.contourPaths = [];
      return;
    }

    const cols = 140;
    const aspectRatio = this.imageHeight / Math.max(this.imageWidth, 1);
    const rows = Math.max(2, Math.round(cols * aspectRatio));
    const values = new Float32Array(cols * rows);
    const width = this.containerWidth;
    const height = this.containerHeight;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const sampleX = (x / Math.max(cols - 1, 1)) * width;
        const sampleY = (y / Math.max(rows - 1, 1)) * height;
        const value = (this.heatmap as any)?.getValueAt?.({ x: sampleX, y: sampleY });
        values[y * cols + x] = typeof value === 'number' ? value : 0;
      }
    }

    const contourGenerator = d3Contours().size([cols, rows]).thresholds(this.thresholds);
    const contourFeatures = contourGenerator(Array.from(values));
    this.contourPaths = this.buildPathsFromContourFeatures(contourFeatures, cols, rows);
  }

  private async createSciChartSurface(rootElement: string | HTMLDivElement): Promise<void> {
    if (this.sciChartSurface) {
      return;
    }
    this.configureSciChartWasm();
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(rootElement);
    this.sciChartSurface = sciChartSurface;

    sciChartSurface.background = 'transparent';

    const xAxis = new NumericAxis(wasmContext);
    xAxis.isVisible = false;
    xAxis.drawMajorGridLines = false;
    xAxis.drawMinorGridLines = false;
    xAxis.visibleRange = new NumberRange(0, Math.max(this.imageWidth, 1));

    const yAxis = new NumericAxis(wasmContext);
    yAxis.isVisible = false;
    yAxis.drawMajorGridLines = false;
    yAxis.drawMinorGridLines = false;
    yAxis.flippedCoordinates = true;
    yAxis.visibleRange = new NumberRange(0, Math.max(this.imageHeight, 1));

    sciChartSurface.xAxes.add(xAxis);
    sciChartSurface.yAxes.add(yAxis);

    const dataSeries = new UniformHeatmapDataSeries(wasmContext, {
      xStart: 0,
      xStep: 1,
      yStart: 0,
      yStep: 1,
      zValues: [[0]]
    });

    const heatmapSeries = new UniformHeatmapRenderableSeries(wasmContext, {
      dataSeries,
      colorMap: this.buildSciChartColorMap(),
      useLinearTextureFiltering: true,
      opacity: this.clampedHeatmapOpacity()
    });

    sciChartSurface.renderableSeries.add(heatmapSeries);
    this.sciChartDataSeries = dataSeries;
    this.sciChartSeries = heatmapSeries;
  }

  private renderSciChart(): void {
    if (!this.sciChartSurface || !this.sciChartDataSeries) {
      return;
    }
    this.updateSciChartHeatmap();
  }

  private updateSciChartHeatmap(): void {
    if (!this.sciChartSurface || !this.sciChartDataSeries || !this.sciChartSeries) {
      return;
    }

    if (!this.points?.length) {
      this.sciChartDataSeries.setZValues([[0]]);
      this.sciChartDataSeries.xStep = 1;
      this.sciChartDataSeries.yStep = 1;
      this.sciChartSeries.colorMap.minimum = 0;
      this.sciChartSeries.colorMap.maximum = 0;
      this.sciChartSurface.zoomExtents();
      return;
    }

    const { zValues, xStep, yStep, min, max } = this.buildSciChartGrid();
    this.sciChartDataSeries.setZValues(zValues);
    this.sciChartDataSeries.xStart = 0;
    this.sciChartDataSeries.yStart = 0;
    this.sciChartDataSeries.xStep = xStep;
    this.sciChartDataSeries.yStep = yStep;

    this.sciChartSeries.colorMap.minimum = min;
    this.sciChartSeries.colorMap.maximum = max;
    this.sciChartSeries.colorMap.gradientStops = this.buildSciChartGradientStops();

    this.updateSciChartOpacity();
    this.sciChartSurface.zoomExtents();
  }

  private buildSciChartGrid(): { zValues: number[][]; xStep: number; yStep: number; min: number; max: number } {
    const cols = 200;
    const aspectRatio = this.imageHeight / Math.max(this.imageWidth, 1);
    const rows = Math.max(2, Math.round(cols * aspectRatio));
    const xStep = Math.max(this.imageWidth / Math.max(cols - 1, 1), 1);
    const yStep = Math.max(this.imageHeight / Math.max(rows - 1, 1), 1);
    const zValues = Array.from({ length: rows }, () => new Array<number>(cols).fill(0));

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (let y = 0; y < rows; y++) {
      const sampleY = y * yStep;
      for (let x = 0; x < cols; x++) {
        const sampleX = x * xStep;
        const value = this.interpolateValue(sampleX, sampleY);
        zValues[y][x] = value;
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }

    if (!isFinite(min) || !isFinite(max)) {
      min = 0;
      max = 0;
    }

    return { zValues, xStep, yStep, min, max };
  }

  private buildSciChartGradientStops(): TGradientStop[] {
    const max = this.thresholds[this.thresholds.length - 1];
    return this.thresholds.map((threshold, index) => ({
      offset: Math.min(1, Math.max(0, threshold / Math.max(max, 1))),
      color: this.palette[index] ?? this.palette[this.palette.length - 1]
    }));
  }

  private buildSciChartColorMap(): HeatmapColorMap {
    return new HeatmapColorMap({
      minimum: this.thresholds[0],
      maximum: this.thresholds[this.thresholds.length - 1],
      gradientStops: this.buildSciChartGradientStops()
    });
  }

  private configureSciChartWasm(): void {
    if (this.sciChartConfigured) {
      return;
    }
    SciChartSurface.configure({
      wasmUrl: `https://cdn.jsdelivr.net/npm/scichart@${libraryVersion}/_wasm/scichart2d.wasm`
    });
    this.sciChartConfigured = true;
  }

  private updateSciChartOpacity(): void {
    if (this.sciChartSeries) {
      this.sciChartSeries.opacity = this.clampedHeatmapOpacity();
    }
  }

  private clampedHeatmapOpacity(): number {
    return Math.min(1, Math.max(0, this.heatmapOpacity ?? 1));
  }

  // Genera una griglia densa di punti adattata al container per la heatmap.
  private generateHeatmapField(): { dataPoints: { x: number; y: number; value: number }[] } {
    const targetSpacing = 40; // px in native image space
    const cols = Math.max(20, Math.round(this.imageWidth / targetSpacing));
    const aspectRatio = this.imageHeight / Math.max(this.imageWidth, 1);
    const rows = Math.max(2, Math.round(cols * aspectRatio));
    const width = this.containerWidth;
    const height = this.containerHeight;
    const cellMap = new Map<string, { x: number; y: number; value: number; count: number }>();
    const dataPoints: { x: number; y: number; value: number }[] = [];

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const sampleX = (col / (cols - 1)) * this.imageWidth;
        const sampleY = (row / (rows - 1)) * this.imageHeight;
        const value = this.interpolateValue(sampleX, sampleY);
        const x = Math.round((col / (cols - 1)) * width);
        const y = Math.round((row / (rows - 1)) * height);
        const key = `${x}:${y}`;
        const existing = cellMap.get(key);

        if (existing) {
          existing.value = (existing.value * existing.count + value) / (existing.count + 1);
          existing.count += 1;
        } else {
          cellMap.set(key, { x, y, value, count: 1 });
        }
      }
    }

    cellMap.forEach((entry) => {
      dataPoints.push({ x: entry.x, y: entry.y, value: entry.value });
    });

    return { dataPoints };
  }

  // Adatta blur/raggio/palette di heatmap.js quando cambiano dimensioni o palette.
  private updateHeatmapAppearance(): void {
    if (!this.heatmap || !this.containerWidth || !this.containerHeight) {
      return;
    }
    this.enableHeatmapWillReadFrequently();
    const { radius, blur, maxOpacity } = this.computeHeatmapAppearance();
    const gradient = this.buildGradient();
    (this.heatmap as HeatmapInstance & { configure?: (options: Partial<HeatmapConfiguration>) => void }).configure?.({
      radius,
      blur,
      gradient,
      maxOpacity
    });
  }

  private enableHeatmapWillReadFrequently(): void {
    const renderer = (this.heatmap as unknown as { _renderer?: any })?._renderer;
    const canvas: HTMLCanvasElement | undefined = renderer?.canvas ?? renderer?._canvas;
    const shadowCanvas: HTMLCanvasElement | undefined = renderer?.shadowCanvas ?? renderer?._shadowCanvas;
    if (canvas) {
      const ctx = canvas.getContext('2d', { willReadFrequently: true } as any);
      if (ctx) {
        renderer.ctx = ctx;
      }
    }
    if (shadowCanvas) {
      const shadowCtx = shadowCanvas.getContext('2d', { willReadFrequently: true } as any);
      if (shadowCtx) {
        renderer.shadowCtx = shadowCtx;
      }
    }
  }

  private resizeOverlays(): void {
    // Heatmap.js gestisce internamente il ridimensionamento dei propri canvas.
  }

  // Raggio base proporzionato al lato minore del viewer.
  private computeHeatmapRadius(): number {
    const base = Math.min(this.containerWidth, this.containerHeight);
    return Math.max(50, Math.round(base / 8));
  }

  private computeHeatmapAppearance(): { radius: number; blur: number; maxOpacity: number } {
    const baseRadius = this.computeHeatmapRadius();
    if (this.mode === 'heatmap-soft') {
      return {
        radius: Math.max(40, Math.round(baseRadius * 0.65)),
        blur: 0.8,
        maxOpacity: 0.85
      };
    }
    return {
      radius: baseRadius,
      blur: 0.96,
      maxOpacity: 0.7
    };
  }

  // Costruisce la mappa gradient -> colore a partire dalle soglie configurate.
  private buildGradient(): Record<number, string> {
    const gradient: Record<number, string> = {};
    const min = this.thresholds[0];
    const max = this.thresholds[this.thresholds.length - 1];
    const range = Math.max(1, max - min);
    this.thresholds.forEach((threshold, index) => {
      const stop = Math.min(1, Math.max(0, (threshold - min) / range));
      gradient[parseFloat(stop.toFixed(2))] = this.palette[index] ?? this.palette[this.palette.length - 1];
    });
    return gradient;
  }

  // Produce le isobande scalando la griglia logica via d3-contour.
  private renderContoursD3(): void {
    if (!this.points?.length) {
      this.contourPaths = [];
      return;
    }

    const cols = 180;
    const aspectRatio = this.imageHeight / Math.max(this.imageWidth, 1);
    const rows = Math.max(2, Math.round(cols * aspectRatio));
    const values = new Array(cols * rows);
    const stepX = this.imageWidth / (cols - 1);
    const stepY = this.imageHeight / (rows - 1);

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const sampleX = x * stepX;
        const sampleY = y * stepY;
        values[y * cols + x] = this.interpolateValue(sampleX, sampleY);
      }
    }

    const contourGenerator = d3Contours().size([cols, rows]).thresholds(this.thresholds);
    const contourFeatures = contourGenerator(values);

    this.contourPaths = this.buildPathsFromContourFeatures(contourFeatures, cols, rows);
  }

  private buildPathsFromContourFeatures(contourFeatures: any[], cols: number, rows: number): ContourPath[] {
    const width = this.containerWidth;
    const height = this.containerHeight;
    const projection = geoTransform({
      point: function (this: any, x: number, y: number) {
        const projectedX = (x / (cols - 1)) * width;
        const projectedY = (y / (rows - 1)) * height;
        this.stream.point(projectedX, projectedY);
      }
    });
    const pathCreator = geoPath(projection);

    const paths: ContourPath[] = [];
    for (const feature of contourFeatures) {
      const d = pathCreator(feature) ?? '';
      if (!d) {
        continue;
      }
      const paletteIndex = this.getPaletteIndex(feature.value as number);
      const color = this.palette[Math.min(paletteIndex, this.palette.length - 1)];
      paths.push({ d, color, value: feature.value as number });
    }
    return paths;
  }

  // Alternativa senza d3: marching squares via libreria dedicata.
  private renderContoursMarchingSquares(): void {
    if (!this.points?.length) {
      this.contourPaths = [];
      return;
    }

    const cols = 140;
    const aspectRatio = this.imageHeight / Math.max(this.imageWidth, 1);
    const rows = Math.max(2, Math.round(cols * aspectRatio));
    const stepX = this.imageWidth / (cols - 1);
    const stepY = this.imageHeight / (rows - 1);
    const grid: number[][] = Array.from({ length: rows }, () => new Array<number>(cols).fill(0));

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const sampleX = x * stepX;
        const sampleY = y * stepY;
        grid[y][x] = this.interpolateValue(sampleX, sampleY);
      }
    }

    const width = this.containerWidth;
    const height = this.containerHeight;
    const scaleX = width / Math.max(cols - 1, 1);
    const scaleY = height / Math.max(rows - 1, 1);
    const paths: ContourPath[] = [];

    // Iso-bande piene per ogni soglia tramite marching squares.
    const lowerBounds = this.thresholds.slice(0, -1);
    const bandWidths = lowerBounds.map((threshold, idx) => this.thresholds[idx + 1] - threshold);
    const bandCollections = marchingIsoBands(grid, lowerBounds, bandWidths, { polygons: true, noFrame: true }) as
      | number[][][]
      | number[][][][];

    for (let i = 0; i < lowerBounds.length; i++) {
      const band = (bandCollections as number[][][][])[i] ?? [];
      const level = lowerBounds[i];
      const paletteIndex = this.getPaletteIndex(level);
      const color = this.palette[Math.min(paletteIndex, this.palette.length - 1)];

      for (const ring of band) {
        const d = this.buildPathFromRing(ring, scaleX, scaleY);
        if (d) {
          paths.push({ d, color, value: level });
        }
      }
    }

    // Fallback a isolinee se nessuna banda viene restituita (limite estremo).
    if (!paths.length) {
      for (const threshold of this.thresholds) {
        const lines = marchingIsoContours(grid, threshold, { noFrame: true }) as number[][][];
        for (const ring of lines ?? []) {
          const d = this.buildPathFromRing(ring, scaleX, scaleY);
          if (d) {
            const paletteIndex = this.getPaletteIndex(threshold);
            const color = this.palette[Math.min(paletteIndex, this.palette.length - 1)];
            paths.push({ d, color, value: threshold });
          }
        }
      }
    }

    this.contourPaths = paths;
  }

  private buildPathFromRing(ring: number[][], scaleX: number, scaleY: number): string {
    if (!ring?.length) {
      return '';
    }
    const commands: string[] = [];
    const [startX, startY] = ring[0];
    commands.push(`M ${startX * scaleX} ${startY * scaleY}`);
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = ring[i];
      commands.push(`L ${x * scaleX} ${y * scaleY}`);
    }
    commands.push('Z');
    return commands.join(' ');
  }

  private getPaletteIndex(value: number): number {
    const thresholdIndex = this.thresholds.indexOf(value);
    const fallbackIndex = this.thresholds.findIndex((t) => value <= t);
    return thresholdIndex >= 0 ? thresholdIndex : fallbackIndex >= 0 ? fallbackIndex : this.palette.length - 1;
  }

  private interpolateValue(sampleX: number, sampleY: number): number {
    // Interpolazione IDW per stimare il segnale in coordinate arbitrarie.
    let numerator = 0;
    let denominator = 0;
    const power = 2;
    const smoothing = 0.0001;

    for (const point of this.points) {
      const dx = sampleX - point.x;
      const dy = sampleY - point.y;
      const distanceSq = dx * dx + dy * dy;

      if (distanceSq === 0) {
        return point.value;
      }

      const weight = 1 / Math.pow(distanceSq + smoothing, power / 2);
      numerator += weight * point.value;
      denominator += weight;
    }

    return denominator === 0 ? 0 : numerator / denominator;
  }

  private isHeatmapJsMode(): boolean {
    return this.mode === 'heatmap' || this.mode === 'heatmap-soft';
  }

  private isD3ContourMode(): boolean {
    return this.mode === 'contours' || this.mode === 'contours-lines';
  }

  private pointerToImageCoords(event: PointerEvent): { relativeX: number; relativeY: number; imageX: number; imageY: number } {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const relativeX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    const relativeY = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
    const imageX = (relativeX / Math.max(rect.width, 1)) * this.imageWidth;
    const imageY = (relativeY / Math.max(rect.height, 1)) * this.imageHeight;
    return { relativeX, relativeY, imageX, imageY };
  }

  private endApDrag(): void {
    this.draggingAp = undefined;
    this.draggingObstacle = undefined;
    if (typeof this.draggingPointerId === 'number') {
      this.containerRef.nativeElement.releasePointerCapture?.(this.draggingPointerId);
    }
    this.draggingPointerId = undefined;
  }
}
